import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getWorkspaceFormConfig, WORKSPACE_OVERVIEW_FORM_SCHEMA } from '@constants';
import { ApiEndpoint, FormType, ServiceModalName, Translation } from '@enums';
import { useAppDispatch, useCreateModal } from '@hooks';
import { getWorkspaceOptions, submitFormRequest } from '@store';
import type { FormConfig, WorkspaceConfigItem, WsConfigOverviewForm, WsConfigOverviewProps } from '@types';

import i18n from '@translations';
import { WsConfigCard } from '../components';

export const WsConfigOverview: React.FC<WsConfigOverviewProps> = ({ workspaceDetails }) => {
  const { id: application_id } = useParams();
  const dispatch = useAppDispatch();

  const formType = FormType.Workspace;
  const schema = WORKSPACE_OVERVIEW_FORM_SCHEMA;

  const { t: tCommon } = useTranslation(Translation.Common, { keyPrefix: 'button' });
  const { t: tForms } = useTranslation(Translation.Workspace, { keyPrefix: 'forms' });
  const { t: tTabs } = useTranslation(Translation.Workspace, { keyPrefix: 'tabs' });
  const { t: tButtons } = useTranslation(Translation.Workspace, { keyPrefix: 'buttons' });

  const configItems: WorkspaceConfigItem[] = [
    {
      left: tForms('workspaceName'),
      right: workspaceDetails?.name,
    },
    {
      left: tForms('memberCount'),
      right: workspaceDetails?.permissions?.length,
    },
  ];

  const formConfig: FormConfig = useMemo(
    () =>
      getWorkspaceFormConfig({
        endpoint: ApiEndpoint.DEFAULT,
        schema,
      }),
    [schema, i18n.language],
  );

  const initialValues: WsConfigOverviewForm = useMemo(
    () => ({
      name: workspaceDetails?.name ?? '',
    }),
    [workspaceDetails, tCommon],
  );

  const handleSubmit = useCallback(
    (values: WsConfigOverviewForm) => {
      dispatch(
        submitFormRequest({
          formType,
          formData: values,
          params: {
            content_id: application_id,
          },
          globalOptions: {
            returnResult: true,
            content: true,
            endpoint: ApiEndpoint.CONFIGURATION,
          },
          callbackFunction: {
            refetch: getWorkspaceOptions(),
          },
        }),
      );
    },
    [application_id, formType, dispatch],
  );

  const { openModal, isSubmitting } = useCreateModal({
    modalName: ServiceModalName.FormModal,
    formType,
    formConfig,
    initialValues,
    handleSubmit,
  });

  return (
    <WsConfigCard
      label={{ title: tTabs('workspaceOverview'), confirmButton: tButtons('updateDetails') }}
      isSubmitting={isSubmitting}
      infoItems={configItems}
      callbackFunction={{
        ...(workspaceDetails?.is_owner && { click: openModal }),
      }}
    />
  );
};
